// src/components/SEO.js
import React from 'react';
import { Helmet } from 'react-helmet';

const comprehensiveBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "name": "Grizzly Tree Company",
  "image": "https://www.grizzlytreecompany.com/logo.svg",
  "url": "https://www.grizzlytreecompany.com",
  "telephone": "+17753095630",
  "email": "grizzlytreeco.99@gmail.com",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "1640 Lantana Dr.",
    "addressLocality": "Minden",
    "addressRegion": "NV",
    "postalCode": "89423"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 39.0097,
    "longitude": -119.7554
  },
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": [
      "Monday",
      "Tuesday", 
      "Wednesday", 
      "Thursday", 
      "Friday"
    ],
    "opens": "08:00",
    "closes": "17:00"
  },
  "areaServed": [
    "Reno",
    "Sparks",
    "Carson City",
    "Gardnerville",
    "Minden",
    "Lake Tahoe",
    "Spanish Springs",
    "Washoe County",
    "Douglas County"
  ],
  "serviceType": [
    "Tree Pruning",
    "Tree Removal",
    "Stump Grinding",
    "Emergency Services",
    "Tree Health",
    "Land Clearing"
  ]
};

const SEO = ({ 
  page = 'home', 
  additionalMetadata = {},
  serviceSchema = null 
}) => {
  const baseUrl = 'https://www.grizzlytreecompany.com';
  
  const seoConfig = {
    home: {
      title: "Grizzly Tree Company - Top Arborist Services in Reno, Nevada",
      description: "Premier tree care experts serving Northern Nevada. Professional arborist services including tree removal, pruning, health management, and emergency tree care in Reno, Gardnerville, and Lake Tahoe region.",
      keywords: "arborist, tree service, Reno Nevada, tree removal, tree pruning, forest management, Northern Nevada tree care, emergency tree services",
      canonicalPath: '/',
      ogImage: '/images/og-home.jpg',
      jsonLd: {
        "@type": "LocalBusiness",
        "name": "Grizzly Tree Company",
        "description": "Expert tree care services in Northern Nevada",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "1640 Lantana Dr.",
          "addressLocality": "Minden",
          "addressRegion": "NV",
          "postalCode": "89423"
        },
        "telephone": "+17753095630",
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 39.0097,
          "longitude": -119.7554
        }
      }
    },
    aboutUs: {
      title: "About Grizzly Tree Company - Experienced Arborists in Nevada",
      description: "Learn about Grizzly Tree Company's team of professional arborists with backgrounds in wildland firefighting. Serving Reno, Gardnerville, and Northern Nevada since our founding.",
      keywords: "arborist team, tree care experts, Northern Nevada tree service, professional arborists, wildland firefighters",
      canonicalPath: '/about-us',
      ogImage: '/images/og-about.jpg',
      jsonLd: {
        "@type": "AboutPage",
        "name": "About Grizzly Tree Company",
        "description": "Our team of expert arborists and tree care professionals"
      }
    },
    contact: {
      title: "Contact Grizzly Tree Company - Free Tree Service Estimate in Reno",
      description: "Contact our expert tree care team for a free estimate. Professional arborist services in Reno, Gardnerville, and Northern Nevada.",
      keywords: "tree service quote, free estimate, Reno tree care, contact arborists, Northern Nevada tree services",
      canonicalPath: '/contact',
      ogImage: '/images/og-contact.jpg',
      jsonLd: {
        "@type": "ContactPage",
        "name": "Contact Grizzly Tree Company",
        "description": "Get in touch for professional tree care services"
      }
    },
    crew: {
      title: "Meet Our Crew - Professional Arborists in Northern Nevada",
      description: "Our experienced team of arborists and tree care specialists. From former wildland firefighters to skilled climbers, we bring expertise to every job.",
      keywords: "arborist team, tree care professionals, Northern Nevada tree experts, skilled climbers, wildland firefighters",
      canonicalPath: '/crew',
      ogImage: '/images/og-crew.jpg',
      jsonLd: {
        "@type": "ProfilePage",
        "name": "Grizzly Tree Company Crew",
        "description": "Our professional team of tree care experts"
      }
    },
    gallery: {
      title: "Grizzly Tree Company - Tree Service Project Gallery",
      description: "View our recent tree care and forest management projects in Northern Nevada. See the quality and expertise of Grizzly Tree Company in action.",
      keywords: "tree service projects, arborist work, Northern Nevada tree care, before and after, tree management gallery",
      canonicalPath: '/gallery',
      ogImage: '/images/og-gallery.jpg',
      jsonLd: {
        "@type": "ImageGallery",
        "name": "Grizzly Tree Company Project Gallery",
        "description": "Gallery of our professional tree care projects"
      }
    },
    services: {
      title: "Comprehensive Tree Care Services in Reno, Nevada",
      description: "Professional tree services including pruning, removal, health management, emergency services, and land clearing. Expert arborists serving Northern Nevada.",
      keywords: "tree pruning, tree removal, tree health, emergency tree services, land clearing, Reno arborist, Northern Nevada tree care",
      canonicalPath: '/services',
      ogImage: '/images/og-services.jpg',
      jsonLd: {
        "@type": "Service",
        "name": "Grizzly Tree Company Services",
        "description": "Comprehensive professional tree care services"
      }
    }
  };

  // Merge base config with any additional metadata
  const pageConfig = {
    ...seoConfig[page],
    ...additionalMetadata
  };



  return (
    <Helmet>
      {/* Standard Meta Tags */}
      <title>{pageConfig.title}</title>
      <meta name="description" content={pageConfig.description} />
      <meta name="keywords" content={pageConfig.keywords} />

      {/* Canonical URL */}
      <link 
        rel="canonical" 
        href={`${baseUrl}${pageConfig.canonicalPath}`} 
      />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={pageConfig.title} />
      <meta property="og:description" content={pageConfig.description} />
      <meta 
        property="og:url" 
        content={`${baseUrl}${pageConfig.canonicalPath}`} 
      />
      <meta 
        property="og:image" 
        content={`${baseUrl}${pageConfig.ogImage}`} 
      />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageConfig.title} />
      <meta name="twitter:description" content={pageConfig.description} />
      <meta 
        name="twitter:image" 
        content={`${baseUrl}${pageConfig.ogImage}`} 
      />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          ...pageConfig.jsonLd,
          "url": `${baseUrl}${pageConfig.canonicalPath}`,
          "image": `${baseUrl}${pageConfig.ogImage}`
        })}
      </script>

      {/* Geo Tags for Local SEO */}
      <meta name="geo.region" content="US-NV" />
      <meta name="geo.placename" content="Reno" />
      <meta name="geo.position" content="39.5296;-119.8138" />
      <meta name="ICBM" content="39.5296, -119.8138" />

      {/* Local Business Schema */}
      <script type="application/ld+json">
        {JSON.stringify(
          serviceSchema 
            ? { 
                ...comprehensiveBusinessSchema, 
                ...serviceSchema 
              }
            : comprehensiveBusinessSchema
        )}
      </script>

      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
    </Helmet>
  );
};

export default SEO;